/* ============================================================
   src/shared/recents.ts
   ============================================================
   The "recently opened estimates" list, in localStorage.

   Lifted verbatim out of App.tsx so the mobile shell can write to
   and read from the SAME list — the storage key is unchanged, so
   every list already on a machine survives the move. Desktop fills
   it on save/open; mobile fills it when a .est is opened out of
   Files/Dropbox. Home renders it on both.

   Deliberately per-device rather than per-account: it is a "what
   was I just doing on THIS machine" list, and the entries carry
   whole estimate snapshots, which don't belong in a synced store.
   ============================================================ */

export type RecentFile = { name: string; json: any; ts: number };

export const RECENTS_KEY = "du_recent_files_v1";
export const RECENTS_MAX = 8;

export function getRecents(): RecentFile[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function saveRecents(next: RecentFile[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next.slice(0, RECENTS_MAX)));
  } catch {}
}

/** Move `name` to the front of the list with a fresh timestamp, replacing any
 *  earlier entry under the same name so reopening a file doesn't stack up
 *  duplicates. Never throws — a full or disabled localStorage must not be able
 *  to break opening a file. */
export function pushRecent(name: string, json: any) {
  const now = Date.now();
  const prev = getRecents();
  const filtered = prev.filter((r) => r?.name !== name);
  saveRecents([{ name, json, ts: now }, ...filtered]);
}

export function clearRecents() {
  try {
    localStorage.removeItem(RECENTS_KEY);
  } catch {}
}
